import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

import { ITextInputProps } from './ITextInputProps'
import { TextInput } from './TextInput'
import {
  creditCardMask,
  currencyMask,
  cvvMask,
  dateMask,
  documentNumberMask,
  floatMask,
  integerMask,
  monthYearMask,
  phoneMask,
  zipCodeMask,
} from './utils/mask'

interface Props extends ITextInputProps {
  mask?:
    | 'float'
    | 'integer'
    | 'money'
    | 'zipCode'
    | 'creditCard'
    | 'numberString'
    | 'monthYear'
    | 'phone'
    | 'cvv'
    | 'cpf'
    | 'date'
  onChange: (value: any) => void
}

export interface IMaskedInput {
  setValue: (value: any) => void
}

// Given the raw browser value and the caret position within it, finds which
// digit index (0-based) the caret is after, then locates that same digit in
// the masked output — keeping the caret logically in the same place even after
// mask separators are inserted or removed.
function adjustedCaretPos(rawValue: string, rawCaret: number, masked: string): number {
  // Count how many digits are before the caret in the raw (browser) value
  let digitsBefore = 0
  for (let i = 0; i < rawCaret; i++) {
    if (/\d/.test(rawValue[i])) digitsBefore++
  }

  // Find the position in the masked string after that many digits
  let found = 0
  for (let i = 0; i <= masked.length; i++) {
    if (found === digitsBefore) return i
    if (i < masked.length && /\d/.test(masked[i])) found++
  }
  return masked.length
}

// Sanitises a free-form float string: keeps digits and the first comma only.
function floatInputMask(value: string): string {
  const withoutInvalid = value.replace('.', ',').replace(/[^0-9,]/g, '')
  const commaIndex = withoutInvalid.indexOf(',')
  if (commaIndex === -1) return withoutInvalid
  const intPart = withoutInvalid.slice(0, commaIndex)
  const decPart = withoutInvalid.slice(commaIndex + 1).replace(/,/g, '').slice(0, 2)
  return intPart + ',' + decPart
}

const Component: React.ForwardRefRenderFunction<IMaskedInput, Props> = (
  { value, onChange, mask, ...props },
  ref,
) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const caretPosRef = useRef<number | null>(null)

  const [inputData, setInputData] = useState<string>(() => {
    switch (mask) {
      case 'integer':
        return integerMask(String(value ?? 0))
      case 'float':
        if (typeof value === 'number') {
          return floatMask(value.toFixed(2))
        }
        return floatMask(String(value ?? 0))
      case 'money':
        return currencyMask(String(value ?? 0))
      case 'phone':
        return phoneMask(String(value ?? ''))
      case 'monthYear':
        return monthYearMask(String(value ?? ''))
      case 'date':
        return dateMask(String(value ?? ''))
      case 'cpf':
        return documentNumberMask(String(value ?? ''))
      case 'cvv':
        return cvvMask(String(value ?? ''))
      default:
        return ''
    }
  })

  // Restore caret after every render that sets caretPosRef
  useLayoutEffect(() => {
    if (caretPosRef.current === null) return
    const el = inputRef.current
    if (!el) return
    const pos = caretPosRef.current
    el.setSelectionRange(pos, pos)
    caretPosRef.current = null
  })

  const handleChange = useCallback(
    (v: string, rawCaret?: number) => {
      let input = String(v)

      switch (mask) {
        case 'creditCard':
          input = creditCardMask(input)
          break
        case 'numberString':
          input = integerMask(input)
          break
        case 'integer':
          input = integerMask(input || '0')
          break
        case 'money':
          input = currencyMask(input)
          break
        case 'zipCode':
          input = zipCodeMask(input)
          break
        case 'phone':
          input = phoneMask(input)
          break
        case 'cpf':
          input = documentNumberMask(input)
          break
        case 'monthYear':
          input = monthYearMask(input)
          break
        case 'date':
          input = dateMask(input)
          break
        case 'cvv':
          input = cvvMask(input)
          break
        case 'float':
          input = floatInputMask(input)
          break
        default:
          input = floatMask(input || '0')
      }

      if (rawCaret !== undefined) {
        caretPosRef.current =
          mask === 'float' ? rawCaret : adjustedCaretPos(v, rawCaret, input)
      }

      setInputData(input)

      if (mask === 'float') {
        onChange && onChange(parseFloat(input.replace(',', '.')))
      } else if (mask === 'integer') {
        onChange && onChange(parseInt(input))
      } else if (mask === 'money') {
        onChange && onChange(parseFloat(floatMask(input).replace(',', '.')))
      } else {
        onChange && onChange(input)
      }
    },
    [onChange, mask],
  )

  useImperativeHandle(ref, () => ({
    setValue(value) {
      handleChange(value)
    },
  }))

  return (
    <TextInput
      {...props}
      ref={inputRef}
      value={inputData}
      onChange={(e) => {
        const caret = e.target.selectionStart ?? undefined
        handleChange(e.target.value, caret)
      }}
    />
  )
}

export const MaskedInput = forwardRef(Component)
